import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Instagram, MapPin, Phone, Mail } from "lucide-react";
import { useLang } from "@/lib/i18n";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/r44rmpev_20F28E74-64C5-4EDC-8C90-95F901ADECAB.png";

function TikTokIcon({ size = 16, className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
    </svg>
  );
}

function Logo() {
  return (
    <Link to="/" data-testid="logo-home-link" className="flex items-center min-w-0 shrink-0">
      <img loading="lazy" decoding="async"         src={LOGO_URL}
        alt="Soho69"
        className="block w-12 h-12 md:w-14 md:h-14 object-contain select-none"
        draggable={false}
        style={{
          // mix-blend-mode: screen drops the PNG's black background reliably on iOS Safari
          mixBlendMode: "screen",
          filter:
            "drop-shadow(0 0 10px rgba(255,27,141,0.7)) drop-shadow(0 0 22px rgba(255,27,141,0.4))",
        }}
      />
    </Link>
  );
}

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="lang-toggle"
      className="inline-flex items-center rounded-full p-1 shrink-0"
      style={{
        background: "rgba(0,0,0,0.55)",
        border: "1px solid rgba(255,255,255,0.15)",
        boxShadow: "0 0 12px rgba(255,27,141,0.25)",
      }}
    >
      {["es", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`px-2.5 md:px-3 py-1 text-[10px] md:text-xs font-bold rounded-full transition-all uppercase ${
            lang === l
              ? "text-white shadow-[0_0_16px_rgba(255,27,141,0.55)]"
              : "text-white/80 hover:text-white"
          }`}
          style={
            lang === l ? { background: "linear-gradient(135deg, #FF1B8D 0%, #FF6BC6 100%)" } : {}
          }
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();

  // Hide header wordmark only on home hero (when scrolled near top)
  React.useEffect(() => {
    const update = () => {
      const isHomeTop = pathname === "/" && window.scrollY < 100;
      document.body.classList.toggle("home-at-top", isHomeTop);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => {
      window.removeEventListener("scroll", update);
      document.body.classList.remove("home-at-top");
    };
  }, [pathname]);

  const NAV = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/events", label: t("nav.events"), key: "events" },
    { to: "/gallery", label: t("nav.gallery"), key: "gallery" },
    { to: "/book?type=barorder", label: t("nav.vip"), key: "vip" },
    { to: "/drinks-tickets", label: t("nav.tickets"), key: "tickets" },
    { to: "/contact", label: t("nav.contact"), key: "contact" },
  ];

  return (
    <div className="min-h-screen text-white">
      <header
        className="sticky top-0 z-50 border-b border-white/5"
        style={{ background: "#000" }}
      >
        <div className="max-w-[1400px] mx-auto px-3 md:px-12 h-16 md:h-20 flex items-center justify-between gap-2 md:gap-4">
          <Logo />
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.key}`}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.28em] font-medium transition-colors whitespace-nowrap ${
                    isActive ? "text-neon" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <LangToggle />
            <Link
              to="/book?type=barorder"
              data-testid="nav-book-cta"
              className="btn-pink !py-2 !px-4 text-[10px] md:text-xs uppercase tracking-[0.22em] hidden md:inline-flex"
            >
              {t("nav.reserve")}
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              className="lg:hidden text-white p-1"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[100] bg-plum-900/95 backdrop-blur-2xl flex flex-col overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-plum-900/95 backdrop-blur">
            <Logo />
            <button
              onClick={() => setOpen(false)}
              data-testid="mobile-menu-close"
              className="text-white p-1"
              aria-label="Close menu"
            >
              <X size={28} />
            </button>
          </div>
          <nav className="flex-1 flex flex-col p-6 gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.key}`}
                className={({ isActive }) =>
                  `font-heading text-3xl tracking-wide ${isActive ? "text-neon" : "text-white"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/book?type=barorder"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-reserve"
              className="btn-pink mt-4"
            >
              {t("nav.reserveTable")}
            </Link>
          </nav>
        </div>
      )}

      <main key={pathname} className="overflow-x-hidden">
        <Outlet />
      </main>

      <footer className="mt-20 md:mt-24 border-t border-white/10 bg-plum-800/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-16 grid md:grid-cols-4 gap-10">
          <div>
            <Logo />
            <p className="mt-4 text-sm text-white/85 leading-relaxed">{t("foot.about")}</p>
          </div>
          <div>
            <h4 className="font-heading text-xl text-neon uppercase tracking-wider mb-4">
              {t("foot.visit")}
            </h4>
            <p className="text-sm text-white/70 flex items-start gap-2">
              <MapPin size={16} className="mt-0.5 text-neon shrink-0" />
              <a href="https://maps.google.com/?q=Soho69+Tenerife" target="_blank" rel="noreferrer noopener" className="hover:text-neon transition">
                C.C. Verónicas 3 · Playa de las Américas
              </a>
            </p>
            <p className="text-sm text-white/85 mt-2">{t("foot.hours")}</p>
          </div>
          <div>
            <h4 className="font-heading text-xl text-neon uppercase tracking-wider mb-4">
              {t("foot.contact")}
            </h4>
            <p className="text-sm text-white/70 flex items-center gap-2">
              <Phone size={16} className="text-neon shrink-0" /> +34 611 37 97 36
            </p>
            <a
              href="mailto:soho69tenerife@gmail.com"
              data-testid="footer-email"
              className="text-sm text-white/70 flex items-center gap-2 mt-2 hover:text-neon whitespace-nowrap"
            >
              <Mail size={16} className="text-neon shrink-0" /> soho69tenerife@gmail.com
            </a>
            <a
              href="https://instagram.com/soho69tfs"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-instagram"
              className="text-sm text-white/70 flex items-center gap-2 mt-2 hover:text-neon"
            >
              <Instagram size={16} className="text-neon shrink-0" /> @soho69tfs
            </a>
            <a
              href="https://www.tiktok.com/@soho69tenerie"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-tiktok"
              className="text-sm text-white/70 flex items-center gap-2 mt-2 hover:text-neon"
            >
              <TikTokIcon size={16} className="text-neon shrink-0" /> @soho69tenerie
            </a>
          </div>
          <div>
            <h4 className="font-heading text-xl text-neon uppercase tracking-wider mb-4">
              {t("foot.staff")}
            </h4>
            <Link
              to="/login"
              data-testid="footer-staff-login"
              className="text-sm text-white/70 hover:text-neon"
            >
              {t("foot.staffLogin")}
            </Link>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-[10px] md:text-xs text-white/70 tracking-widest uppercase px-4">
          © {new Date().getFullYear()} Soho69 · {t("foot.rights")}
          <div className="mt-2 normal-case tracking-wider">
            <a
              href="https://onlineofficesolutions.co"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-site-credit"
              className="text-white/70 hover:text-white/90"
            >
              Site by Online Office Solutions
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
